import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { SweetAlertShow } from "../../../master/Master";
import { studentBatchNFeeStatusChange } from "../../../../api/StudentDetails";

// Status 2 remains the existing backend value for inactive records,
// displayed to staff as "Drop Out". Status 3 is the existing delete action.
// Status 4 (Transferred) requires backend support if it is not enabled yet.
const STATUS_OPTIONS = [
  {
    value: "1",
    label: "Active",
    description: "Student is studying in this batch.",
    icon: "ri-user-follow-line",
    tone: "green",
  },
  {
    value: "2",
    label: "Drop Out",
    description: "Student left this batch; keep their record.",
    icon: "ri-user-unfollow-line",
    tone: "orange",
  },
  {
    value: "4",
    label: "Transferred",
    description: "Student has moved to another batch.",
    icon: "ri-arrow-left-right-line",
    tone: "blue",
  },
  {
    value: "5",
    label: "Completed",
    description: "Student successfully finished this batch.",
    icon: "ri-checkbox-circle-line",
    tone: "purple",
  },
  {
    value: "3",
    label: "Delete",
    description: "Permanently remove this enrollment record.",
    icon: "ri-delete-bin-6-line",
    tone: "red",
  },
];

const todayLocal = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getErrorMessage = (error) =>
  error?.response?.data?.message ||
  error?.response?.message ||
  error?.message ||
  "Unable to update student status.";

export default function BatchNFeeStatusModal({ batch, onClose, onUpdated }) {
  const initialStatus = String(batch?.status ?? "1");
  const [status, setStatus] = useState(initialStatus);
  const [batchEndDate, setBatchEndDate] = useState(
    batch?.batch_end_date || todayLocal(),
  );
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const canDelete = Number(batch?.total_paid || 0) === 0;
  const options = useMemo(
    () => STATUS_OPTIONS.filter((option) => option.value !== "3" || canDelete),
    [canDelete],
  );
  const selected = STATUS_OPTIONS.find((option) => option.value === status);
  const existing = STATUS_OPTIONS.find((option) => option.value === initialStatus);
  const isDelete = status === "3";
  const isCompleted = status === "5";
  const reasonRequired = ["2", "3", "4"].includes(status);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !saving) onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, saving]);

  const changeStatus = (nextStatus) => {
    if (saving) return;
    setStatus(nextStatus);
    setErrors({});
  };

  const validate = () => {
    const nextErrors = {};
    if (!selected) nextErrors.status = "Please select a valid status.";
    if (isDelete && !canDelete) {
      nextErrors.status = "You cannot delete an enrollment with payments.";
    }
    if (isCompleted && !batchEndDate) {
      nextErrors.batchEndDate = "Please select the batch completion date.";
    }
    if (reasonRequired && !reason.trim()) {
      nextErrors.reason = "Please enter a reason for this status change.";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleUpdateStatus = async () => {
    if (saving || !validate()) return;

    if (isDelete) {
      const confirmation = await Swal.fire({
        icon: "warning",
        title: "Delete student enrollment?",
        text: "This action permanently removes the enrollment record and cannot be undone.",
        showCancelButton: true,
        confirmButtonText: "Yes, delete enrollment",
        cancelButtonText: "Keep record",
        confirmButtonColor: "#dc3545",
        reverseButtons: true,
        focusCancel: true,
        allowOutsideClick: false,
      });
      if (!confirmation.isConfirmed) return;
    }

    setSaving(true);
    try {
      // Preserve the API keys and save flow used by the existing component.
      const payload = {
        student_batch_n_fee_id: batch?.id,
        batch_id: batch?.batch_id,
        student_id: batch?.student_id,
        status,
        batch_end_date: isCompleted ? batchEndDate : null,
        reason: reason.trim() || null,
      };

      const response = await studentBatchNFeeStatusChange(payload);
      if (response?.data?.status) {
        SweetAlertShow(
          "success",
          "Status updated",
          response?.data?.message || "Student status updated successfully.",
        );
        onUpdated?.(response.data);
        onClose?.();
      } else {
        SweetAlertShow(
          "info",
          "Update not completed",
          response?.data?.message || "Please try again.",
        );
      }
    } catch (error) {
      SweetAlertShow("error", "Error", getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const studentName = batch?.student_info?.name || batch?.student?.name || "Student";
  const registration = batch?.student_info?.registration_no || batch?.student?.registration_no;
  const courseName = batch?.course?.title || batch?.course_title || "";
  const batchName = batch?.batch?.title || batch?.batch_title || "";

  return (
    <div
      className="modal d-block scai-change-status-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !saving) onClose?.();
      }}
    >
      <style>{`
        .scai-change-status-backdrop{position:fixed;inset:0;z-index:1090;overflow-y:auto;background:rgba(19,30,55,.6);padding:20px 12px;backdrop-filter:blur(3px)}
        .scai-change-status-dialog{max-width:720px;width:100%;margin:clamp(10px,5vh,55px) auto;background:#fff;border-radius:18px;box-shadow:0 24px 75px rgba(16,29,54,.28);overflow:hidden;color:#26334d}
        .scai-change-status-head{padding:23px 26px 18px;border-bottom:1px solid #eef0f5;display:flex;justify-content:space-between;align-items:flex-start;gap:16px}
        .scai-change-status-heading{display:flex;align-items:center;gap:12px}
        .scai-change-status-heading-icon{width:44px;height:44px;flex-shrink:0;display:grid;place-items:center;background:#edf1ff;color:#5969dc;border-radius:13px;font-size:23px}
        .scai-change-status-heading h4{font-size:19px;font-weight:750;margin:0 0 4px}
        .scai-change-status-heading p{color:#7b8698;font-size:12.5px;margin:0}
        .scai-change-status-close{width:33px;height:33px;border:1px solid #ebedf1;border-radius:9px;background:#fff;color:#66738a;font-size:18px;cursor:pointer}
        .scai-change-status-body{padding:20px 26px 22px}
        .scai-change-status-context{display:flex;flex-wrap:wrap;gap:8px;margin-bottom:18px}
        .scai-change-status-pill{border:1px solid #e9edf4;background:#f7f9fd;border-radius:8px;padding:7px 10px;font-size:12px;color:#5b657a;display:inline-flex;gap:5px;align-items:center}
        .scai-change-status-pill strong{color:#26334d}
        .scai-change-status-label{display:flex;justify-content:space-between;align-items:center;gap:10px;font-size:13px;font-weight:700;color:#344158;margin:0 0 10px}
        .scai-change-status-label small{font-size:11px;font-weight:400;color:#8892a3}
        .scai-change-status-options{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px}
        .scai-change-status-option{width:100%;display:flex;align-items:flex-start;gap:11px;text-align:left;background:#fff;border:1.5px solid #e6eaf1;border-radius:12px;padding:13px;cursor:pointer;transition:background .15s,border-color .15s,box-shadow .15s}
        .scai-change-status-option:hover{border-color:#a5b1ee;background:#fafbff}
        .scai-change-status-option:focus-visible{outline:3px solid #a0adfa;outline-offset:2px}
        .scai-change-status-option.is-selected{border-color:#6576ed;background:#f4f6ff;box-shadow:0 0 0 2px rgba(101,118,237,.08)}
        .scai-change-status-option:disabled{opacity:.7;cursor:not-allowed}
        .scai-change-status-option-icon{flex-shrink:0;width:36px;height:36px;border-radius:10px;display:grid;place-items:center;font-size:19px}
        .scai-tone-green{background:#eaf8ef;color:#218b4d}.scai-tone-orange{background:#fff4e5;color:#c67a1b}.scai-tone-blue{background:#eaf3ff;color:#326dd0}.scai-tone-purple{background:#f2ecff;color:#8057d5}.scai-tone-red{background:#ffedef;color:#d73e51}
        .scai-change-status-option-text{flex:1;min-width:0}
        .scai-change-status-option-title{font-size:13px;font-weight:750;display:flex;align-items:center;gap:7px;color:#27334c}
        .scai-change-status-option-desc{font-size:11.5px;line-height:1.5;color:#7d8899;margin-top:3px}
        .scai-change-status-check{color:#5f70e7;font-size:17px}
        .scai-change-status-option.is-danger.is-selected{background:#fff6f6;border-color:#ea7781}
        .scai-change-status-note{border-radius:9px;background:#f5f7fa;color:#677386;padding:10px 12px;font-size:12px;line-height:1.5;margin-top:13px;display:flex;gap:8px;align-items:flex-start}
        .scai-change-status-note i{font-size:17px;flex-shrink:0}
        .scai-change-status-fields{display:grid;gap:15px;margin-top:19px}
        .scai-change-status-field label{font-size:13px;font-weight:700;color:#364258;margin-bottom:7px;display:block}
        .scai-change-status-field .form-control{border-radius:10px;border-color:#dce2ec;font-size:13px;padding:11px 12px}
        .scai-change-status-field .form-control:focus{border-color:#6679e8;box-shadow:0 0 0 3px rgba(102,121,232,.12)}
        .scai-change-status-error{font-size:12px;color:#dc3545;margin-top:6px}
        .scai-change-status-foot{padding:15px 26px;background:#fafbfe;border-top:1px solid #eef0f5;display:flex;justify-content:space-between;align-items:center;gap:12px}
        .scai-change-status-current{font-size:12px;color:#7b8799}
        .scai-change-status-actions{display:flex;gap:9px;justify-content:flex-end;flex-wrap:wrap}
        .scai-change-status-actions .btn{border-radius:9px;font-size:13px;font-weight:650;padding:10px 15px}
        @media(max-width:600px){.scai-change-status-backdrop{padding:10px}.scai-change-status-dialog{margin:5px auto;border-radius:13px}.scai-change-status-head,.scai-change-status-body{padding:16px}.scai-change-status-foot{padding:13px 16px;align-items:stretch;flex-direction:column}.scai-change-status-options{grid-template-columns:1fr}.scai-change-status-current{align-self:flex-start}.scai-change-status-actions .btn{flex:1}}
      `}</style>
      <div className="scai-change-status-dialog" role="dialog" aria-modal="true" aria-labelledby="scai-change-status-title">
        <div className="scai-change-status-head">
          <div className="scai-change-status-heading">
            <div className="scai-change-status-heading-icon"><i className="ri-user-settings-line" /></div>
            <div>
              <h4 id="scai-change-status-title">Update Student Status</h4>
              <p>Choose how this student should appear in the batch.</p>
            </div>
          </div>
          <button type="button" aria-label="Close status modal" className="scai-change-status-close" onClick={onClose} disabled={saving}><i className="ri-close-line" /></button>
        </div>
        <div className="scai-change-status-body">
          <div className="scai-change-status-context">
            <span className="scai-change-status-pill"><i className="ri-user-3-line" /><strong>{studentName}</strong>{registration ? ` · ${registration}` : ""}</span>
            {courseName && <span className="scai-change-status-pill"><i className="ri-book-open-line" />{courseName}</span>}
            {batchName && <span className="scai-change-status-pill"><i className="ri-team-line" />{batchName}</span>}
          </div>

          <div className="scai-change-status-label"><span>Select Status <span className="text-danger">*</span></span><small>Select one option</small></div>
          <div className="scai-change-status-options" role="radiogroup" aria-label="Student batch status">
            {options.map((option) => {
              const checked = status === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={checked}
                  disabled={saving}
                  onClick={() => changeStatus(option.value)}
                  className={`scai-change-status-option ${checked ? "is-selected" : ""} ${option.value === "3" ? "is-danger" : ""}`}
                >
                  <span className={`scai-change-status-option-icon scai-tone-${option.tone}`}><i className={option.icon} /></span>
                  <span className="scai-change-status-option-text">
                    <span className="scai-change-status-option-title">{option.label}</span>
                    <span className="scai-change-status-option-desc">{option.description}</span>
                  </span>
                  {checked && <i className="ri-checkbox-circle-fill scai-change-status-check" aria-hidden="true" />}
                </button>
              );
            })}
          </div>
          {errors.status && <div className="scai-change-status-error">{errors.status}</div>}
          {!canDelete && <div className="scai-change-status-note"><i className="ri-information-line" />Delete is unavailable because payments have been recorded for this enrollment.</div>}
          {status === "2" && <div className="scai-change-status-note"><i className="ri-information-line" />Drop Out retains the student and enrollment history. It uses the existing inactive status value (2).</div>}
          {isDelete && <div className="scai-change-status-note"><i className="ri-alert-line" />Deleting permanently removes the enrollment. You will be asked to confirm.</div>}
          {status === "4" && <div className="scai-change-status-note"><i className="ri-information-line" />Transferred updates the status only. It does not automatically enroll the student in another batch.</div>}

          <div className="scai-change-status-fields">
            {isCompleted && (
              <div className="scai-change-status-field">
                <label htmlFor="scai-completion-date">Batch End Date <span className="text-danger">*</span></label>
                <input id="scai-completion-date" className={`form-control ${errors.batchEndDate ? "is-invalid" : ""}`} type="date" value={batchEndDate} disabled={saving} onChange={(e) => {setBatchEndDate(e.target.value);setErrors((prev) => ({...prev,batchEndDate:undefined}));}} />
                {errors.batchEndDate && <div className="scai-change-status-error">{errors.batchEndDate}</div>}
              </div>
            )}
            <div className="scai-change-status-field">
              <label htmlFor="scai-change-reason">Reason / Remarks {reasonRequired ? <span className="text-danger">*</span> : <span className="text-muted fw-normal">(optional)</span>}</label>
              <textarea id="scai-change-reason" className={`form-control ${errors.reason ? "is-invalid" : ""}`} rows={3} disabled={saving} value={reason} placeholder={isDelete ? "Why is this enrollment being deleted?" : status === "2" ? "Reason for student dropout..." : status === "4" ? "Reason for transfer..." : "Enter a note about this status change..."} onChange={(e) => {setReason(e.target.value);setErrors((prev) => ({...prev,reason:undefined}));}} />
              {errors.reason && <div className="scai-change-status-error">{errors.reason}</div>}
            </div>
          </div>
        </div>
        <div className="scai-change-status-foot">
          <span className="scai-change-status-current">Current: <strong>{existing?.label || `Status ${initialStatus}`}</strong></span>
          <div className="scai-change-status-actions">
            <button type="button" className="btn btn-outline-secondary" disabled={saving} onClick={onClose}><i className="ri-close-line me-1" />Cancel</button>
            <button type="button" className={`btn ${isDelete ? "btn-danger" : "btn-primary"}`} disabled={saving} onClick={handleUpdateStatus}>
              {saving ? <><span className="spinner-border spinner-border-sm me-2" />{isDelete ? "Deleting..." : "Saving..."}</> : <><i className={`${isDelete ? "ri-delete-bin-6-line" : "ri-save-line"} me-1`} />{isDelete ? "Delete Enrollment" : "Save Status"}</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
